// src/components/CashBook.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  writeBatch,
  doc,
  orderBy,
  limit
} from "firebase/firestore";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * CashBook.jsx — full
 * Business rules applied:
 *  - Outward / Receipt  => DEBIT (full)
 *  - Payment            => CREDIT (full)
 *  - Inward             => ignored (not shown / not counted)
 * Other features:
 *  - robust extractor handling many field name variants
 *  - debounce on filters
 *  - safer Firestore reads (try date-range queries first)
 *  - compute opening balance (skips inward)
 *  - persist displayed rows to cashbook idempotent (batched)
 */

// ---------- helpers ----------
function round2(n) { return Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100; }
const currencyFmt = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtAmount(n) {
  if (n === undefined || n === null || n === 0) return "";
  return currencyFmt.format(Number(n));
}
function fmtDate(d) {
  if (!d) return "";
  try {
    if (typeof d.toDate === "function") return d.toDate().toISOString().slice(0, 10);
    const dt = new Date(d);
    if (!isNaN(dt)) return dt.toISOString().slice(0, 10);
    return String(d).slice(0, 10);
  } catch (e) { return ""; }
}
function balanceLabel(n) {
  const v = round2(Number(n || 0));
  const abs = Math.abs(v);
  if (v === 0) return "0.00";
  if (v > 0) return `${currencyFmt.format(abs)} Dr.`;
  return `${currencyFmt.format(abs)} Cr.`;
}

function normalizeInputDate(v) {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return "";
}

function parseNumberFlexible(x) {
  if (x === undefined || x === null || x === "") return 0;
  if (typeof x === "number") return x;
  let s = String(x).trim();
  s = s.replace(/[^0-9.\-,()]/g, "");
  if (s === "") return 0;
  let negative = false;
  if (s.startsWith("(") && s.endsWith(")")) { negative = true; s = s.slice(1, -1); }
  s = s.replace(/,/g, "");
  if (s.endsWith("-")) { negative = true; s = s.slice(0, -1); }
  const num = Number(s);
  if (Number.isFinite(num)) return negative ? -num : num;
  return 0;
}

/**
 * extractAmountsFromDoc (enhanced)
 * - maps outward/receipt -> debit
 * - payment -> credit
 * - inward -> returns { ignore: true } so caller can skip
 * - tries many common field names and rows/entries
 */
function extractAmountsFromDoc(r) {
  const toNum = v => parseNumberFlexible(v);
  if (!r || typeof r !== "object") return { debit: 0, credit: 0 };

  // normalize strings for detection
  const typeStr = (r?.type || "").toString().toUpperCase();
  const classStr = (r?.transactionClass || "").toString().toUpperCase();
  const modeStr = (r?.mode || "").toString().toUpperCase();
  const miscStr = ((r?.voucherType || r?.voucher || "") || "").toString().toUpperCase();

  const hasInwardFlag = Boolean(
    typeStr.includes("INWARD") ||
    classStr.includes("INWARD") ||
    r?.inward
  );
  if (hasInwardFlag) return { debit: 0, credit: 0, ignore: true };

  const isOutward = Boolean(typeStr.includes("OUTWARD") || classStr.includes("OUTWARD") || r?.outward);
  const isReceipt = Boolean(
    typeStr.includes("RECEIPT") ||
    classStr.includes("RECEIPT") ||
    miscStr.includes("RECEIPT") ||
    r?.receipt || r?.received || r?.receivedAmount || r?.receivedAmt || modeStr.includes("CASH")
  );
  const isPayment = Boolean(
    typeStr.includes("PAYMENT") ||
    classStr.includes("PAYMENT") ||
    miscStr.includes("PAYMENT") ||
    r?.payment || r?.paidAmount || r?.paymentAmount || r?.paymentAmt || r?.paidAmt
  );
  const isJournal = Boolean(
    typeStr.includes("JOURNAL") ||
    classStr.includes("JOURNAL") ||
    miscStr.includes("JOURNAL") ||
    Array.isArray(r?.journalEntries) || Array.isArray(r?.entries) || Array.isArray(r?.rows)
  );

  // try common total fields
  const totalCandidates = [
    r?.totals?.net, r?.totals?.netAmount, r?.totals?.gross, r?.grandTotal,
    r?.total, r?.amount, r?.value, r?.invoiceAmount, r?.saleAmount, r?.net,
    r?.paymentAmount, r?.paidAmount, r?.paymentAmt,
    r?.receivedAmount, r?.receivedAmt, r?.receipt, r?.receiptAmount
  ];
  let total = 0;
  for (const c of totalCandidates) {
    if (c !== undefined && c !== null && String(c).trim() !== "") { total = toNum(c); break; }
  }

  // aggregate rows if total not found
  if (!total) {
    const rows = r.rows || r.items || r.journalEntries || r.entries || [];
    if (Array.isArray(rows) && rows.length) {
      let sum = 0;
      for (const row of rows) {
        if (!row) continue;
        const a = row?.amount ?? row?.value ?? row?.total ?? row?.debit ?? row?.credit ?? row?.net;
        if (a !== undefined && a !== null && String(a).trim() !== "") {
          sum += Math.abs(toNum(a));
          continue;
        }
        if (row.quantity && row.rate) {
          sum += toNum(row.quantity) * toNum(row.rate);
        }
      }
      if (sum) total = round2(sum);
    }
  }

  // explicit debit/credit if present
  const explicitDebit = (
    r?.debit ?? r?.debitAmt ?? r?.debit_amount ?? r?.receipt ?? r?.receivedAmount ?? r?.received
  );
  const explicitCredit = (
    r?.credit ?? r?.creditAmt ?? r?.credit_amount ?? r?.payment ?? r?.paidAmount ?? r?.paymentAmount
  );

  if (explicitDebit !== undefined && explicitDebit !== null && String(explicitDebit).trim() !== "") {
    return { debit: round2(toNum(explicitDebit)), credit: 0 };
  }
  if (explicitCredit !== undefined && explicitCredit !== null && String(explicitCredit).trim() !== "") {
    return { debit: 0, credit: round2(toNum(explicitCredit)) };
  }

  // Map rules:
  if (isOutward || isReceipt) {
    return { debit: round2(total || 0), credit: 0 };
  }
  if (isPayment) {
    return { debit: 0, credit: round2(total || 0) };
  }

  // Journal handling
  if (isJournal && Array.isArray(r.rows || r.journalEntries || r.entries)) {
    const rows = r.rows || r.journalEntries || r.entries || [];
    let drSum = 0, crSum = 0;
    for (const row of rows) {
      const a = row?.amount ?? row?.value ?? row?.debit ?? row?.credit ?? row?.net;
      if (a === undefined || a === null || String(a).trim() === "") continue;
      const v = toNum(a);
      const marker = (row?.drcr || row?.type || row?.side || "").toString().toUpperCase();
      if (marker.includes("DR") || marker.includes("DEBIT") || (row.debit && !row.credit)) drSum += v;
      else if (marker.includes("CR") || marker.includes("CREDIT") || (row.credit && !row.debit)) crSum += v;
      else {
        if (row.debit) drSum += v;
        else if (row.credit) crSum += v;
        else drSum += v; // default to debit if uncertain
      }
    }
    const net = round2(drSum - crSum);
    if (net > 0) return { debit: net, credit: 0 };
    if (net < 0) return { debit: 0, credit: Math.abs(net) };
    // no net -> fallback to total later
  }

  // fallback: if total available put to credit by default (changeable)
  if (total) {
    return { debit: 0, credit: round2(total) };
  }

  return { debit: 0, credit: 0 };
}

function isBankDoc(r) {
  if (!r || typeof r !== "object") return false;
  const blob = [
    r.mode,
    r.paymentMode,
    r.payMode,
    r.paymentType,
    r.transactionMode,
    r.type,
    r.transactionClass,
    r.voucherType,
    r.voucher,
    r.narration,
  ]
    .map((x) => String(x || "").toUpperCase())
    .join(" ");
  if (/\b(BANK|NEFT|RTGS|IMPS|UPI|CHEQUE|CHQ|ONLINE|TRANSFER)\b/.test(blob)) return true;
  if (r.bankName || r.bankAccount || r.ifsc || r.accountNo) return true;
  return false;
}

// compute opening balance up to dateExclusive (YYYY-MM-DD)
async function loadSalesImportInto(target) {
  try {
    const { loadSalesImportInvoiceLikeDocs } = await import("../services/salesImportTransactionDataService");
    const list = await loadSalesImportInvoiceLikeDocs({ maxWeeks: 52 });
    list.forEach((inv) =>
      target.push({
        id: inv.id,
        __source: "sales_import",
        ...inv,
        date: inv.date || inv.invoiceDateStr,
        type: inv.type || "outward",
      })
    );
  } catch (e) {
    console.warn("loadSalesImportInto failed", e);
  }
}

async function computeOpeningBalanceUpTo(dateExclusive, sources = ["sales_import", "vouchers", "cashbook"], opts = {}) {
  const bankOnly = Boolean(opts.bankOnly);
  const allDocs = [];
  async function loadColl(name) {
    if (name === "sales_import") {
      await loadSalesImportInto(allDocs);
      return;
    }
    try {
      const snap = await getDocs(collection(db, name));
      snap.forEach(d => allDocs.push({ id: d.id, __source: name, ...d.data() }));
    } catch (e) {
      // ignore
    }
  }
  for (const s of sources) await loadColl(s);
  const beforeDocs = allDocs.filter(d => {
    const ds = fmtDate(d.date || d.createdAt || d.created);
    return ds && ds < dateExclusive;
  });
  let running = 0;
  for (const d of beforeDocs) {
    if (bankOnly && !isBankDoc(d)) continue;
    const amt = extractAmountsFromDoc(d);
    if (amt.ignore) continue; // inward skip
    running += Number(amt.debit || 0) - Number(amt.credit || 0);
  }
  return round2(running);
}

// ---------- component ----------
export default function CashBook({ variant = "cash" }) {
  const isBank = variant === "bank";
  const ledgerCollection = isBank ? "bankbook" : "cashbook";
  const ledgerLabel = isBank ? "Bank book" : "Cash book";

  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [source, setSource] = useState("sales_import"); // sales_import | vouchers | cashbook | bankbook | both

  const mountedRef = useRef(false);
  const fetchTimerRef = useRef(null);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const fetchRows = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const docs = [];
      const fromDateNorm = normalizeInputDate(opts.fromDate ?? fromDate);
      const toDateNorm = normalizeInputDate(opts.toDate ?? toDate);

      async function loadColl(name) {
        try {
          const colRef = collection(db, name);
          if (fromDateNorm && toDateNorm) {
            const q = query(colRef, where("date", ">=", fromDateNorm), where("date", "<=", toDateNorm));
            const snap = await getDocs(q);
            snap.forEach(d => docs.push({ id: d.id, __source: name, ...d.data() }));
            return;
          } else if (fromDateNorm) {
            const q = query(colRef, where("date", ">=", fromDateNorm));
            const snap = await getDocs(q);
            snap.forEach(d => docs.push({ id: d.id, __source: name, ...d.data() }));
            return;
          } else if (toDateNorm) {
            const q = query(colRef, where("date", "<=", toDateNorm));
            const snap = await getDocs(q);
            snap.forEach(d => docs.push({ id: d.id, __source: name, ...d.data() }));
            return;
          } else {
            try {
              const q = query(colRef, orderBy("date", "desc"), limit(200));
              const snap = await getDocs(q);
              snap.forEach(d => docs.push({ id: d.id, __source: name, ...d.data() }));
              return;
            } catch (e) { /* fallback */ }
          }
          const snapAll = await getDocs(colRef);
          snapAll.forEach(d => docs.push({ id: d.id, __source: name, ...d.data() }));
        } catch (e) {
          console.warn("loadColl warning", name, e.message || e);
        }
      }

      if (source === "sales_import" || source === "both") await loadColl("sales_import");
      if (source === "vouchers" || source === "both") await loadColl("vouchers");
      if (source === ledgerCollection) await loadColl(ledgerCollection);

      let normalizedAll = docs.map(d => {
        const dateStr = fmtDate(d.date || d.createdAt || d.created);
        const amt = extractAmountsFromDoc(d);
        return {
          id: d.id,
          source: d.__source,
          raw: d,
          voucherNo: d.voucherNo ?? d.voucherNoNum ?? d.id,
          party: d.party || d.partyName || d.partyDetails?.partyName || d.items?.[0]?.party || "",
          narration: d.narration || `${d.transactionClass || d.type || d.voucherType || ""}${d.voucherNo ? " | " + d.voucherNo : ""}`,
          date: dateStr,
          _debit: amt.debit,
          _credit: amt.credit,
          _ignore: amt.ignore || false
        };
      });

      if (isBank) {
        normalizedAll = normalizedAll.filter((row) => isBankDoc(row.raw));
      }

      // apply date filter client-side
      let filtered = normalizedAll;
      if (fromDateNorm && toDateNorm) filtered = normalizedAll.filter(r => r.date >= fromDateNorm && r.date <= toDateNorm);
      else if (fromDateNorm) filtered = normalizedAll.filter(r => r.date >= fromDateNorm);
      else if (toDateNorm) filtered = normalizedAll.filter(r => r.date <= toDateNorm);

      // skip inward docs explicitly
      filtered = filtered.filter(r => !r._ignore);

      // search filter
      const s = (opts.search ?? search).toLowerCase();
      if (s) {
        filtered = filtered.filter(r =>
          (r.voucherNo || "").toString().toLowerCase().includes(s) ||
          (r.party || "").toString().toLowerCase().includes(s) ||
          (r.narration || "").toString().toLowerCase().includes(s)
        );
      }

      // sort
      filtered.sort((a, b) => {
        if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
        if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
        return 0;
      });

      // compute opening
      let opening = 0;
      if (fromDateNorm) {
        const srcs =
          source === "both"
            ? ["sales_import", "vouchers"]
            : source === ledgerCollection
              ? [ledgerCollection]
              : [source];
        const srcsSafe = srcs.filter(Boolean).length ? srcs : ["sales_import"];
        opening = await computeOpeningBalanceUpTo(fromDateNorm, srcsSafe, { bankOnly: isBank });
      }

      // running balance
      let running = Number(opening || 0);
      const withBal = [];
      if (fromDateNorm) {
        withBal.push({
          id: "__opening__",
          voucherNo: "",
          party: "",
          narration: `Opening balance (closing before ${fromDateNorm})`,
          date: fromDateNorm,
          _debit: 0,
          _credit: 0,
          balance: round2(running),
          source: "opening"
        });
      }

      for (const r of filtered) {
        running += Number(r._debit || 0) - Number(r._credit || 0);
        withBal.push({ ...r, balance: round2(running) });
      }

      if (mountedRef.current) setRows(withBal);
    } catch (err) {
      console.error("fetchRows error", err);
      if (mountedRef.current) alert("Failed to load data: " + (err.message || err));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fromDate, toDate, search, source, sortDir, isBank, ledgerCollection]);

  // debounce fetching on user inputs
  useEffect(() => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => { fetchRows(); }, 300);
    return () => { if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current); };
  }, [fromDate, toDate, search, source, sortDir, fetchRows]);

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      if (r.id === "__opening__") return acc;
      acc.debit += Number(r._debit || 0);
      acc.credit += Number(r._credit || 0);
      return acc;
    }, { debit: 0, credit: 0 });
  }, [rows]);

  // exports
  function exportExcel() {
    const arr = rows.map(r => ({
      "Vh.Type": r.raw?.transactionClass || r.narration?.split("|")?.[0] || r.raw?.type || r.source || "",
      "Vh.No": r.voucherNo || r.id || "",
      "Date": r.date || "",
      "Particulars": r.narration || r.party || "",
      "Debit": r._debit || 0,
      "Credit": r._credit || 0,
      "Balance": r.balance,
      "Source": r.source || ""
    }));
    const ws = XLSX.utils.json_to_sheet(arr);
    const wb = XLSX.utils.book_new();     XLSX.utils.book_append_sheet(wb, ws, ledgerLabel.replace(/\s+/g, ""));
    XLSX.writeFile(wb, `${ledgerLabel.replace(/\s+/g, "")}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  function exportPDF() {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.width;
    doc.setFontSize(14);
    doc.text(`My Company Pvt Ltd — ${ledgerLabel}`, pageWidth / 2, 30, { align: "center" });
    doc.setFontSize(10);
    doc.text(`Printed: ${new Date().toLocaleString()}`, 40, 46);
    const body = rows.map(r => [
      r.raw?.transactionClass || r.narration?.split("|")?.[0] || r.raw?.type || r.source || "",
      r.voucherNo || r.id || "",
      r.date || "",
      r.narration || r.party || "",
      r._debit ? fmtAmount(r._debit) : "",
      r._credit ? fmtAmount(r._credit) : "",
      r.balance !== undefined ? balanceLabel(r.balance) : "",
      r.source || ""
    ]);
    autoTable(doc, {
      startY: 60,
      head: [["Vh.Type", "Vh.No", "Date", "Particulars", "Debit", "Credit", "Balance", "Source"]],
      body,
      styles: { fontSize: 9 },
      didDrawPage: (dataArg) => {
        // footer
        const str = `Page ${doc.internal.getNumberOfPages()}`;
        doc.setFontSize(9);
        doc.text(str, pageWidth - 40, doc.internal.pageSize.height - 20, { align: "right" });
      }
    });
    doc.save(`${ledgerLabel.replace(/\s+/g, "")}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }


  // persist displayed to cashbook (idempotent)
  async function persistDisplayedToCashbook() {
    setLoading(true);
    try {
      if (!rows || rows.length === 0) { alert("No rows to persist."); setLoading(false); return; }
      const toWrite = rows.filter(r => r.id !== "__opening__").map(r => {
        const srcId = r.raw?.id || r.raw?.voucherNo || (`src-${r.source}-${r.voucherNo || r.id || Date.now()}`);
        return {
          sourceId: srcId,
          date: r.date,
          voucherNo: r.voucherNo || "",
          party: r.party || "",
          narration: r.narration || "",
          receipt: r._debit || 0,
          payment: r._credit || 0,
          createdAt: new Date().toISOString(),
          raw: r.raw
        };
      });

      // check existing by batching 'in' queries (max 10 per in)
      const sourceIds = toWrite.map(t => String(t.sourceId));
      const existing = new Set();
      for (let i = 0; i < sourceIds.length; i += 10) {
        const chunk = sourceIds.slice(i, i + 10);
        try {
          const q = query(collection(db, ledgerCollection), where("sourceId", "in", chunk));
          const snap = await getDocs(q);
          snap.forEach(d => { const dt = d.data(); if (dt && dt.sourceId) existing.add(String(dt.sourceId)); });
        } catch (e) {
          console.warn("batched in-query failed, falling back to full ledger scan", e.message || e);
          const cbSnap = await getDocs(collection(db, ledgerCollection));
          cbSnap.forEach(d => { const dt = d.data(); if (dt && dt.sourceId) existing.add(String(dt.sourceId)); });
          break;
        }
      }

      const toInsert = toWrite.filter(t => !existing.has(String(t.sourceId)));
      if (toInsert.length === 0) { alert("No new rows to persist."); setLoading(false); return; }

      for (let i = 0; i < toInsert.length; i += 200) {
        const batch = writeBatch(db);
        toInsert.slice(i, i + 200).forEach(item => {
          const ref = doc(collection(db, ledgerCollection));
          batch.set(ref, item);
        });
        await batch.commit();
      }
      alert(`Persisted ${toInsert.length} rows to ${ledgerCollection}.`);
    } catch (err) {
      console.error("persist error", err);
      alert("Persist failed: " + (err.message || err));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }
  return (
    <div className="h-screen flex flex-col bg-white">
      <MenuBar />
      <HeaderBar />
      <div className="p-4 flex-1 overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold">Ledger / {ledgerLabel}</h1>
            <label className="text-sm">Source:</label>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="border rounded p-1">
              <option value="sales_import">Sales import</option>
              <option value="vouchers">vouchers</option>
              <option value={ledgerCollection}>{ledgerCollection}</option>
              <option value="both">both (sales import + vouchers)</option>
            </select>
            <button onClick={() => fetchRows()} className="px-2 py-1 bg-blue-600 text-white rounded text-sm" disabled={loading}>Refresh</button>
          </div>

          <div className="flex gap-2">
            <button onClick={exportExcel} className="px-3 py-1 rounded bg-green-700 text-white text-sm" disabled={loading}>Export</button>
            <button onClick={exportPDF} className="px-3 py-1 rounded bg-red-600 text-white text-sm" disabled={loading}>PDF</button>
            <button onClick={() => fetchRows()} className="px-3 py-1 rounded bg-yellow-500 text-white text-sm" disabled={loading}>Compute Balances</button>
            <button onClick={persistDisplayedToCashbook} className="px-3 py-1 rounded bg-indigo-600 text-white text-sm" disabled={loading}>Persist to {ledgerCollection}</button>
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow mb-4 flex gap-2 items-center">
          <label className="text-sm">From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border rounded p-1" />
          <label className="text-sm">To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border rounded p-1" />
          <input placeholder="Search voucher / party / narration" value={search} onChange={(e) => setSearch(e.target.value)} className="border rounded p-1 flex-1" />
          <select value={sortDir} onChange={(e) => setSortDir(e.target.value)} className="border rounded p-1">
            <option value="asc">Asc</option>
            <option value="desc">Desc</option>
          </select>
        </div>

        <div className="bg-white rounded shadow overflow-auto">
          <table className="min-w-full text-xs table-auto">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 border w-36 text-left">Vh. Type</th>
                <th className="px-2 py-2 border w-28 text-left">Vh. No</th>
                <th className="px-2 py-2 border w-24 text-left">Date</th>
                <th className="px-2 py-2 border w-34 text-left">Particulars</th>
                <th className="px-2 py-2 border w-28 text-right">Debit</th>
                <th className="px-2 py-2 border w-28 text-right">Credit</th>
                <th className="px-2 py-2 border w-36 text-right">Balance</th>
                <th className="px-2 py-2 border w-20 text-left">Source</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-6 text-center">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="p-6 text-center">No records</td></tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.id || r.voucherNo || idx} className={r.id === "__opening__" ? "bg-yellow-50 font-semibold" : (idx % 2 === 0 ? "bg-white" : "bg-gray-50")}>
                    <td className="px-2 py-2 border text-left align-top">{r.id === "__opening__" ? "" : (r.raw?.transactionClass || r.narration?.split("|")?.[0] || r.raw?.type || "")}</td>
                    <td className="px-2 py-2 border text-left align-top">{r.voucherNo || r.id || ""}</td>
                    <td className="px-2 py-2 border text-left align-top">{r.date || ""}</td>
                    <td className="px-2 py-2 border text-left align-top">{r.narration || r.party || (r.id === "__opening__" ? `Opening Balance` : "")}</td>
                    <td className="px-2 py-2 border text-right align-top">{r._debit ? fmtAmount(r._debit) : ""}</td>
                    <td className="px-2 py-2 border text-right align-top">{r._credit ? fmtAmount(r._credit) : ""}</td>
                    <td className={`px-2 py-2 border text-right align-top font-semibold ${r.balance < 0 ? "text-red-600" : ""}`}>{r.balance !== undefined ? balanceLabel(r.balance) : ""}</td>
                    <td className="px-2 py-2 border text-left align-top">{r.source || ""}</td>
                  </tr>
                ))
              )}
              <tr className="font-semibold bg-gray-100">
                <td colSpan={4} className="px-2 py-2 border text-right">Totals</td>
                <td className="px-2 py-2 border text-right">{fmtAmount(totals.debit)}</td>
                <td className="px-2 py-2 border text-right">{fmtAmount(totals.credit)}</td>
                <td className="px-2 py-2 border text-right">{rows.length ? balanceLabel(rows[rows.length - 1].balance) : ""}</td>
                <td className="px-2 py-2 border" />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
